import { ChangeDetectionStrategy, Component, Inject, Injector, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { forkJoin, Observable, throwError } from 'rxjs';
import { getDescendantOrSelfIds, toOrganizationTree } from 'src/app/_helpers/org-tree-helper';
import { LoginResponse } from 'src/app/models/loginResponse.model';
import { Lookup } from 'src/app/models/lookup.model';
import { OrganizationLevel } from 'src/app/models/organizationLevel.model';
import { ViewAsDraft, ViewAsLabels, ViewAsSelection } from 'src/app/models/viewAs.model';
import { AuthService } from 'src/app/services/auth.service';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { UserRoleService } from 'src/app/services/userRole.service';
import { ViewAsService } from 'src/app/services/view-as.service';
import { ColumnSpan } from 'src/app/shared/form/columnSpan.model';
import { FormComponent, IFormDialogData } from 'src/app/shared/form/form.component';
import { HierarchicalSelectionFormField, LabelFormField, SingleSelectionFormField } from 'src/app/shared/form/formField.model';
import { VIEW_AS_MAX_ORG_LEVELS, VIEW_AS_TOO_MANY_ORG_LEVELS_MESSAGE } from 'src/app/shared/constants/viewAs.constants';
import nameOf from 'src/app/shared/form/nameOf.helper';

export interface IViewAsDialogData extends IFormDialogData {
    roleId?: string;
}

const VIEW_AS_DIALOG_TITLE = 'View as role';
const VIEW_AS_DIALOG_WIDTH = '560px';

export function openViewAsDialog(dialog: MatDialog, roleId?: string): MatDialogRef<ViewAsDialogComponent> {
    return dialog.open<ViewAsDialogComponent, IViewAsDialogData>(ViewAsDialogComponent, {
        data: { roleId },
        width: VIEW_AS_DIALOG_WIDTH,
        disableClose: true,
    });
}

@Component({
    standalone: false,
    selector: 'app-view-as-dialog',
    templateUrl: './../../shared/form/form.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./../../shared/form/form.component.scss'],
})
export class ViewAsDialogComponent extends FormComponent<ViewAsDraft> implements OnInit {
    roles: Lookup[] = [];
    organizationLevels: OrganizationLevel[] = [];

    constructor(
        dialogRef: MatDialogRef<ViewAsDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: IViewAsDialogData,
        private readonly userRoleService: UserRoleService,
        private readonly organizationLevelService: OrganizationLevelService,
        private readonly viewAsService: ViewAsService,
        private readonly authService: AuthService,
        protected injector: Injector,
    ) {
        // No CRUD service: the dialog is add-only and overrides saveAction to start the preview.
        super(dialogRef, null!, data, VIEW_AS_DIALOG_TITLE, injector);
        this.setTitle(this.translateService.getDynamicTranslation(VIEW_AS_DIALOG_TITLE), false);
        this.setMainButtonText('Start viewing');
    }

    ngOnInit(): void {
        this.isFormLoading = true;
        forkJoin({
            roles: this.userRoleService.getAll(),
            organizationLevels: this.organizationLevelService.getAll(),
        }).subscribe({
            next: ({ roles, organizationLevels }) => {
                this.roles = roles;
                this.organizationLevels = this.toOwnHierarchy(organizationLevels);
                this.entity = this.getNewObject();
                this.initFields();
                this.isFormLoading = false;
            },
            error: (error: unknown) => {
                this.toastService.showError(error);
                this.isFormLoading = false;
            },
        });
    }

    getNewObject(): ViewAsDraft {
        return { roleId: this.data?.roleId, organizationLevelIds: [] };
    }

    validate(): boolean {
        return !!this.entity.roleId;
    }

    initFields(): void {
        this.fields = [
            new SingleSelectionFormField({
                property: nameOf<ViewAsSelection>(x => x.roleId),
                name: 'Role',
                options: this.roles,
                columnSpan: new ColumnSpan(1, 1),
            }),
            new HierarchicalSelectionFormField({
                property: nameOf<ViewAsSelection>(x => x.organizationLevelIds),
                name: 'Organization Level',
                options: toOrganizationTree(this.organizationLevels),
                multiple: true,
                required: false,
                columnSpan: new ColumnSpan(1, 1),
            }),
            new LabelFormField({
                property: nameOf<ViewAsSelection>(x => x.organizationLevelIds),
                name: 'Leave organization levels empty to keep your own. The preview is read-only, and "My" data still refers to your own records.',
                required: false,
                columnSpan: new ColumnSpan(1, 1),
                cssClass: 'info-banner',
            }),
        ];
    }

    override save(): void {
        if (this.entity.organizationLevelIds.length > VIEW_AS_MAX_ORG_LEVELS) {
            this.toastService.displayErrorMessage(
                this.translateService.getDynamicTranslation(VIEW_AS_TOO_MANY_ORG_LEVELS_MESSAGE, { max: VIEW_AS_MAX_ORG_LEVELS }),
            );
            return;
        }
        super.save();
    }

    override saveAction(model: ViewAsDraft): Observable<LoginResponse> {
        const { roleId, organizationLevelIds } = model;
        if (!roleId) {
            return throwError(() => new Error('A role is required to start a preview.'));
        }
        const selection: ViewAsSelection = { roleId, organizationLevelIds };
        return this.viewAsService.start(selection, this.buildLabels(selection));
    }

    // The app reloads into the preview once it starts, so the dialog stays in its loading state rather
    // than closing onto a page that is about to be replaced.
    override afterSaveAction(): void {
    }

    // A preview may narrow the admin's access but never widen it: the API accepts only the admin's
    // assigned levels and their descendants. The visible list also holds their ancestors, so those
    // are dropped. The dialog only opens outside a preview, so these are the admin's own levels.
    private toOwnHierarchy(visibleLevels: OrganizationLevel[]): OrganizationLevel[] {
        const tree = toOrganizationTree(visibleLevels);
        const ownIds = new Set<string>();
        (this.authService.getCurrentUser()?.organizationLevelIds ?? []).forEach(assignedId =>
            getDescendantOrSelfIds(tree, assignedId).forEach(id => ownIds.add(id)),
        );
        return visibleLevels.filter(level => ownIds.has(level.id));
    }

    private buildLabels(model: ViewAsSelection): ViewAsLabels {
        const selectedIds = new Set(model.organizationLevelIds);
        return {
            roleName: this.roles.find(role => role.id === model.roleId)?.name,
            organizationLevelNames: this.organizationLevels
                .filter(level => selectedIds.has(level.id))
                .map(level => level.name),
        };
    }
}
